import { RunMode } from "../../../interface/Pos";
import { getResolvedStoreId } from "./useStoreIdentity";
import { isDevReadOnly } from "../utils/devStoreOverride";

// Run mode ผูกกับ "ร้าน" ไม่ใช่ "เครื่อง"
//
// ของเดิมเก็บที่ localStorage key เดียวคือ `_mode` ทั้งเครื่อง แปลว่าถ้าเครื่องนี้
// ตั้งเป็น main_cashier ของร้าน A ไว้ แล้วมี sign out ไป sign in ร้าน B (เช่นตอน
// เทส) เครื่องจะเด้งเข้าโหมด main_cashier ของร้าน B ทันทีโดยไม่ได้ตั้งใจ —
// ไปแย่งสิทธิ์แคชเชียร์ของร้านที่เขาเปิดขายอยู่จริง
//
// ตอนนี้เก็บแยกเป็น `runMode_{storeId}` ร้านไหนไม่เคยเลือกโหมดบนเครื่องนี้ ก็จะได้
// UNASSIGNED = เด้งไปหน้าเลือกโหมด ไม่ไปยึดโหมดของร้านอื่น
export const LEGACY_RUN_MODE_KEY = "_mode";
export const RUN_MODE_KEY_PREFIX = "runMode_";
/** storeId ที่ "อ้างสิทธิ์" ค่าเดิมจาก `_mode` ไปแล้ว — ร้านอื่นห้าม migrate ซ้ำ */
export const RUN_MODE_MIGRATED_FROM_KEY = "runMode_migratedFrom";

// ---------------------------------------------------------------------------
// ช่วงเปลี่ยนผ่าน (ลบทิ้งได้เมื่อทุกเครื่อง migrate ครบแล้ว)
//
// ยังเขียนค่าลง `_mode` ตัวเดิมควบคู่ไปด้วย เผื่อต้อง rollback build กลับไปเวอร์ชัน
// ที่ยังอ่าน `_mode` เครื่องหน้าร้านจะได้ไม่หลุดโหมดกลางวัน
// วิธีเลิกใช้: ตั้ง LEGACY_WRITE_THROUGH = false แล้วค่อยลบโค้ดที่แตะ
// LEGACY_RUN_MODE_KEY / RUN_MODE_MIGRATED_FROM_KEY ออกทั้งหมด
// ---------------------------------------------------------------------------
const LEGACY_WRITE_THROUGH = true;

/** cache ต่อ storeId — กัน localStorage read รัวๆ จาก pinia subscriber */
const cache = new Map<string, string>();

export const runModeStorageKey = (storeId: string) =>
  `${RUN_MODE_KEY_PREFIX}${storeId}`;

/**
 * ย้ายค่าโหมดเดิมของเครื่องนี้ (`_mode`) มาเป็นของร้าน storeId
 *
 * ย้ายให้ "ร้านแรกที่ boot หลังอัปเดต" เท่านั้น ซึ่งก็คือร้านที่เครื่องนี้ใช้งาน
 * อยู่จริง หลังจากนั้นร้านอื่นที่มา login บนเครื่องเดียวกันจะไม่ได้ค่านี้ติดไป
 *
 * @returns true เมื่อมีการย้ายค่าจริงในรอบนี้
 */
export function migrateRunMode(storeId: string): boolean {
  if (!storeId) return false;

  const key = runModeStorageKey(storeId);
  if (safeGet(key)) return false; // ร้านนี้มีโหมดของตัวเองแล้ว ไม่ต้องยุ่ง

  const legacy = safeGet(LEGACY_RUN_MODE_KEY);
  if (!legacy || legacy === RunMode.UNASSIGNED) return false;

  const claimedBy = safeGet(RUN_MODE_MIGRATED_FROM_KEY);
  if (claimedBy && claimedBy !== storeId) return false; // ร้านอื่นเอาไปแล้ว

  safeSet(key, legacy);
  safeSet(RUN_MODE_MIGRATED_FROM_KEY, storeId);
  cache.delete(storeId);

  console.info(
    `[runMode] migrate "${LEGACY_RUN_MODE_KEY}"=${legacy} -> "${key}"`
  );
  return true;
}

export const useRunningMode = () => {
  // MODE 10 == main cashier, 11 Second Cashier,  -1 not set

  return {
    getMode,
    setMode,
    clear,
    migrate: migrateRunMode,
  };

  function getMode() {
    return devReadOnlyMode(readMode());
  }

  function readMode() {
    const storeId = getResolvedStoreId();
    // ยังไม่รู้ว่าร้านไหน = ไม่ยอมคืนโหมดของใครทั้งนั้น ปลอดภัยกว่าเดาผิดร้าน
    if (!storeId) return RunMode.UNASSIGNED;

    const cached = cache.get(storeId);
    if (cached != null) return cached;

    migrateRunMode(storeId);

    const mode = safeGet(runModeStorageKey(storeId)) || RunMode.UNASSIGNED;
    cache.set(storeId, mode);
    return mode;
  }

  function setMode(mode) {
    const storeId = getResolvedStoreId();
    if (!storeId) {
      console.warn("[runMode] ยังไม่รู้ storeId เลยยังบันทึกโหมดไม่ได้");
      return;
    }

    cache.set(storeId, mode);
    safeSet(runModeStorageKey(storeId), mode);
    safeSet(RUN_MODE_MIGRATED_FROM_KEY, storeId);
    if (LEGACY_WRITE_THROUGH) safeSet(LEGACY_RUN_MODE_KEY, mode);
  }

  function clear() {
    const storeId = getResolvedStoreId();
    if (storeId) {
      cache.set(storeId, RunMode.UNASSIGNED);
      safeRemove(runModeStorageKey(storeId));
    } else {
      cache.clear();
    }

    // ต้องล้าง `_mode` + claim ด้วย ไม่งั้น getMode() รอบถัดไปจะ migrate ค่าเดิม
    // กลับมาให้ใหม่ กด "เปลี่ยนโหมด" แล้วเหมือนไม่มีอะไรเกิดขึ้น
    safeRemove(LEGACY_RUN_MODE_KEY);
    safeRemove(RUN_MODE_MIGRATED_FROM_KEY);
  }
};

/**
 * dev สวมร้านอื่น (ดู utils/devStoreOverride.ts) ใช้ได้แค่ unassigned / terminal_order / audit
 * โหมด cashier ทุกตัวเขียน jobchain ของร้านจริง และ main cashier ยังมีงานเบื้องหลัง
 * ที่เขียนทับข้อมูลร้านเขา — รับ jobRequest / QR order ไปแล้วลบทิ้ง, ดัน tables / live /
 * storeStatus / tableMeta — โหมดอื่นจึงคืนเป็น unassigned (ไปเลือกโหมดใหม่ที่ /select-run-mode)
 * ค่าที่เก็บใน localStorage ไม่เปลี่ยน
 */
function devReadOnlyMode(mode: string): string {
  if (!isDevReadOnly()) return mode;
  return mode === RunMode.TERMINAL_ORDER || mode === RunMode.AUDIT ? mode : RunMode.UNASSIGNED;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota เต็ม — ปล่อยให้โหมดเป็นแค่ค่าใน memory ของ tab นี้
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}
